import type { RowSelectionState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTypedSearchParams } from "react-router-typesafe-routes";

import { useAdminData } from "~/common/contexts";
import { useUsersList } from "~/features/admin/users/hooks";
import { toUserRowList } from "~/features/admin/users/utils/transformers";
import { v2Route } from "~/v2/routes";
import { UsersPresenter } from "./users.presenter";

const DEFAULT_PAGE_SIZE = 25;

export function UsersContainer() {
  const [params, setParams] = useTypedSearchParams(v2Route.admin.users);
  const { data, refetch } = useUsersList();
  const { userSegments, userGroups } = useAdminData();

  const rows = useMemo(() => toUserRowList(data.users), [data.users]);

  // V1 filtered by segment / group *name*, so options carry the name as the value too.
  const segmentOptions = useMemo(() => userSegments.map((s) => ({ value: s.name, label: s.name })), [userSegments]);
  const groupOptions = useMemo(() => userGroups.map((g) => ({ value: g.name, label: g.name })), [userGroups]);
  const organizationOptions = useMemo(
    () =>
      Array.from(new Map(rows.map((row) => [row.organization.shortCode, row.organization])).values())
        .map((organization) => ({
          value: organization.shortCode,
          label: `${organization.name} (${organization.shortCode})`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [rows],
  );
  const productRoleOptions = useMemo(
    () =>
      Array.from(
        new Map(
          rows.flatMap((row) =>
            row.productRoles.map((productRole) => {
              const value = `${productRole.productName}:${productRole.roleName}`;
              return [value, { value, label: `${productRole.productName} / ${productRole.roleName}` }] as const;
            }),
          ),
        ).values(),
      ).sort((a, b) => a.label.localeCompare(b.label)),
    [rows],
  );

  const pageIndex = params.page ?? 0;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  const selectedUser = params.selectedUser ?? null;

  const onPageReset = useCallback(() => {
    setParams((prev) => ({ ...prev, page: undefined }));
  }, [setParams]);

  const onPageIndexChange = useCallback(
    (next: number) => {
      setParams((prev) => ({ ...prev, page: next === 0 ? undefined : next }));
    },
    [setParams],
  );

  const onPageSizeChange = useCallback(
    (next: number) => {
      setParams((prev) => ({
        ...prev,
        pageSize: next === DEFAULT_PAGE_SIZE ? undefined : next,
        page: undefined,
      }));
    },
    [setParams],
  );

  const onSelect = useCallback((id: string) => setParams((prev) => ({ ...prev, selectedUser: id })), [setParams]);

  const onCloseSelected = useCallback(() => setParams((prev) => ({ ...prev, selectedUser: undefined })), [setParams]);

  const onRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const onBulkOrganizationChangeCompleted = useCallback(() => {
    setRowSelection({});
    void refetch();
  }, [refetch]);

  return (
    <UsersPresenter
      rows={rows}
      segmentOptions={segmentOptions}
      groupOptions={groupOptions}
      organizationOptions={organizationOptions}
      productRoleOptions={productRoleOptions}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      onPageReset={onPageReset}
      onRefresh={onRefresh}
      selectedUser={selectedUser}
      onSelect={onSelect}
      onCloseSelected={onCloseSelected}
      rowSelection={rowSelection}
      onRowSelectionChange={setRowSelection}
      onBulkOrganizationChangeCompleted={onBulkOrganizationChangeCompleted}
    />
  );
}
